/**
 * Theme glossary explainer — dialog rendering the API glossary (single source of truth).
 */
import React, { useState } from "react";
import { HelpCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { useAuth } from "../App";
import { API } from "./stats/statsApi";

export const ThemeGlossaryDialog = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (open) => {
    if (!open || data) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/themes/glossary`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog onOpenChange={load}>
      <DialogTrigger asChild>
        <button data-testid="themes-glossary-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-mono text-muted-foreground hover:text-foreground hover:border-sky-500/40 transition-colors">
          <HelpCircle className="w-3.5 h-3.5" /> What do these mean?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="themes-glossary-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">Theme product glossary</DialogTitle>
        </DialogHeader>
        {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {data?.sections?.map((sec) => (
          <div key={sec.id} className="mb-4" data-testid={`glossary-section-${sec.id}`}>
            <h3 className="text-sm font-bold text-amber-300 font-mono mb-2">{sec.title}</h3>
            <div className="space-y-2">
              {sec.items.map((it) => (
                <div key={it.term} className="text-xs leading-relaxed">
                  <span className="font-mono font-bold text-foreground">{it.term}</span>
                  <span className="text-muted-foreground"> — {it.definition}</span>
                  {it.evidence && (
                    <span className="block text-[11px] text-emerald-400/80 font-mono mt-0.5 pl-3 border-l border-emerald-500/30">
                      Evidence: {it.evidence}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        <p className="text-[10px] text-muted-foreground font-mono">
          All evidence: point-in-time monthly formations Jul 2023 – Jun 2026, forward returns vs SPY, equal weight. Same glossary available externally at /api/ext/themes/glossary.
        </p>
      </DialogContent>
    </Dialog>
  );
};
